import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Upload, Shield, ChevronRight } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { API } from "@/lib/api";
import { CAREER } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { IMAGES } from "@/constants/assets";

const STATES = [
  "WA", "OR", "ID", "CA", "NV", "AZ", "UT", "MT", "CO", "TX",
  "NY", "FL", "IL", "AK", "HI", "Other",
];

const initial = {
  first_name: "", last_name: "", phone: "", email: "",
  street_address: "", apartment: "", city: "", state: "", zip_code: "",
  has_security_experience: "", shift_availability: "", message: "",
};

export default function Careers() {
  const [form, setForm] = useState(initial);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [emailDelivered, setEmailDelivered] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.state) return toast.error("Please select a state");
    if (!form.has_security_experience) return toast.error("Please select security experience");
    if (!form.shift_availability) return toast.error("Please select shift availability");

    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v || ""));
      if (resume) data.append("resume", resume);

      const res = await fetch(`${API}/application`, { method: "POST", body: data });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        let msg = err.detail || "Submission failed. Please try again or call 206-742-9195.";
        if (res.status === 422) msg = "Please check your details — some fields are missing or invalid.";
        if (res.status === 413) msg = "Resume file is too large. Max 8MB.";
        throw new Error(typeof msg === "string" ? msg : "Submission failed.");
      }
      const body = await res.json().catch(() => ({}));
      setEmailDelivered(!!body?.email_delivered);
      setSuccess(true);
      setForm(initial);
      setResume(null);
      toast.success("Application submitted. Thank you!");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#000000] text-white">
      {/* Hero */}
      <section className="relative py-24 md:py-28 border-b border-white/8 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.officerInStore} alt="Marco Polo Security officer" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 hero-scrim" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">— Careers</p>
          <h1 className="font-display font-black text-4xl md:text-6xl uppercase leading-[1] text-balance max-w-3xl">
            Join a Team That<br />Protects What Matters<span className="text-red-500">.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">
            Marco Polo Security is hiring dependable, professional security officers across Washington State.
            Apply below to be considered for day, night, or 24/7 positions.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-sm p-6 sm:p-10">
            {success ? (
              <div data-testid={CAREER.successAlert} className="text-center py-8">
                <div className="w-14 h-14 rounded-sm bg-red-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-black text-2xl uppercase">Application Received.</h3>
                <p className="mt-3 text-slate-400">
                  Thanks for applying. If your profile matches an open position, our team will reach out.
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs font-condensed uppercase tracking-widest text-slate-500 border border-white/10 rounded-sm px-3 py-2">
                  <Shield className="w-3.5 h-3.5 text-red-500" />
                  {emailDelivered
                    ? "Notification sent to info@marcopolosecurity.com"
                    : "Saved · Our team has been notified"}
                </div>
                <button
                  onClick={() => { setSuccess(false); setEmailDelivered(null); }}
                  className="mt-6 block mx-auto font-condensed text-sm uppercase tracking-widest text-red-500 hover:text-red-400 border-b border-red-600"
                  data-testid="career-submit-another"
                >
                  Submit Another Application
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8">
                <SectionTitle icon={<Shield className="w-4 h-4" />} title="Personal Information" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="First Name" required>
                    <input required value={form.first_name} onChange={upd("first_name")} data-testid={CAREER.firstName} className={inputCls} />
                  </Field>
                  <Field label="Last Name" required>
                    <input required value={form.last_name} onChange={upd("last_name")} data-testid={CAREER.lastName} className={inputCls} />
                  </Field>
                  <Field label="Phone Number" required>
                    <input required type="tel" value={form.phone} onChange={upd("phone")} data-testid={CAREER.phone} className={inputCls} />
                  </Field>
                  <Field label="Email Address" required>
                    <input required type="email" value={form.email} onChange={upd("email")} data-testid={CAREER.email} className={inputCls} />
                  </Field>
                </div>

                <SectionTitle title="Address" />
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                  <div className="sm:col-span-4">
                    <Field label="Street Address" required>
                      <input required value={form.street_address} onChange={upd("street_address")} data-testid={CAREER.street} className={inputCls} />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Apartment #">
                      <input value={form.apartment} onChange={upd("apartment")} data-testid={CAREER.apartment} className={inputCls} />
                    </Field>
                  </div>
                  <div className="sm:col-span-3">
                    <Field label="City" required>
                      <input required value={form.city} onChange={upd("city")} data-testid={CAREER.city} className={inputCls} />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="State" required>
                      <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                        <SelectTrigger data-testid={CAREER.state} className="bg-[#000000] border-white/15 text-white h-11">
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/15 text-white max-h-64">
                          {STATES.map((s) => (
                            <SelectItem key={s} value={s} className="focus:bg-white/10">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <div className="sm:col-span-1">
                    <Field label="Zip Code" required>
                      <input required value={form.zip_code} onChange={upd("zip_code")} data-testid={CAREER.zip} className={inputCls} />
                    </Field>
                  </div>
                </div>

                <SectionTitle title="Availability" />
                <Field label="Do you have security experience?" required>
                  <RadioGroup
                    value={form.has_security_experience}
                    onValueChange={(v) => setForm({ ...form, has_security_experience: v })}
                    className="flex gap-4"
                    data-testid={CAREER.experience}
                  >
                    {["yes", "no"].map((v) => (
                      <label key={v} className="flex-1 flex items-center gap-3 border border-white/15 rounded-sm px-4 py-3 cursor-pointer hover:border-red-500/50 has-[[data-state=checked]]:border-red-600 has-[[data-state=checked]]:bg-red-600/10 transition-colors">
                        <RadioGroupItem value={v} id={`exp-${v}`} className="border-slate-500 text-red-600" />
                        <span className="font-condensed uppercase text-sm">{v}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </Field>

                <Field label="Shift Availability" required>
                  <RadioGroup
                    value={form.shift_availability}
                    onValueChange={(v) => setForm({ ...form, shift_availability: v })}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                    data-testid={CAREER.shift}
                  >
                    {[
                      { v: "day", l: "Day shift" },
                      { v: "night", l: "Night shift" },
                      { v: "both", l: "Both" },
                    ].map((o) => (
                      <label key={o.v} className="flex items-center gap-3 border border-white/15 rounded-sm px-4 py-3 cursor-pointer hover:border-red-500/50 has-[[data-state=checked]]:border-red-600 has-[[data-state=checked]]:bg-red-600/10 transition-colors">
                        <RadioGroupItem value={o.v} id={`shift-${o.v}`} className="border-slate-500 text-red-600" />
                        <span className="font-condensed uppercase text-sm">{o.l}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </Field>

                <SectionTitle title="Resume & Notes" />
                <Field label="Upload Resume (PDF, DOCX up to 8MB)">
                  <label
                    className="flex items-center gap-3 border border-dashed border-white/15 hover:border-red-500/50 rounded-sm px-4 py-6 cursor-pointer bg-[#000000] transition-colors"
                    data-testid={CAREER.resume}
                  >
                    <div className="w-10 h-10 rounded-sm bg-red-600 flex items-center justify-center">
                      <Upload className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-white">
                        {resume ? resume.name : "Click to upload a file"}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 font-condensed uppercase tracking-widest">
                        {resume ? `${(resume.size / 1024).toFixed(1)} KB` : "PDF · DOC · DOCX"}
                      </div>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => setResume(e.target.files?.[0] || null)}
                    />
                  </label>
                </Field>

                <Field label="Message / Additional Information">
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={upd("message")}
                    data-testid={CAREER.message}
                    className={cn(inputCls, "resize-none")}
                    placeholder="Tell us anything else we should know…"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={CAREER.submit}
                  className="group w-full bg-white hover:bg-slate-100 disabled:opacity-60 text-[#0A0A0A] px-6 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? "Submitting…" : (<>Submit Application <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[#000000] border border-white/15 rounded-sm px-4 py-3 h-11 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-condensed text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}

function SectionTitle({ title, icon }) {
  return (
    <div className="flex items-center gap-2 pt-2 border-t border-white/10 first:border-t-0 first:pt-0">
      {icon && <span className="text-red-500">{icon}</span>}
      <span className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500">{title}</span>
    </div>
  );
}
